import { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { ResizeObserver } from '@juggle/resize-observer';

const RECT_KEYS = [
  "bottom",
  "height",
  "left",
  "right",
  "top",
  "width",
  "x",
  "y"
];
function areRectsNotEqual(rectA, rectB) {
  return rectA !== rectB && RECT_KEYS.some((key) => rectA[key] !== rectB[key]);
}

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function getElementRect(element) {
  const {
    bottom,
    height,
    left,
    right,
    top,
    width,
    x,
    y
  } = element.getBoundingClientRect();
  return {
    bottom,
    height,
    left,
    right,
    top,
    width,
    x,
    y
  };
}

const LISTENER_CONFIG = {
  capture: true,
  passive: true
};
function listenToWindow(eventType, listener) {
  window.addEventListener(eventType, listener, LISTENER_CONFIG);
  function removeListener() {
    window.removeEventListener(eventType, listener, LISTENER_CONFIG);
  }
  return removeListener;
}

function doesEventTargetContainElement(target, element) {
  return target === window && element.isConnected || target instanceof Node && target.contains(element);
}

const INITIAL_RECT = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: 0,
  y: 0
};
function useRect(options = {}) {
  const scroll = Boolean(options.scroll);
  const transitionEnd = Boolean(options.transitionEnd);
  const [element, setElement] = useState(null);
  const [rect, setRect] = useState(INITIAL_RECT);
  const updateRect = useCallback(() => {
    setRect((currentRect) => {
      const nextRect = element ? getElementRect(element) : INITIAL_RECT;
      return areRectsNotEqual(currentRect, nextRect) ? nextRect : currentRect;
    });
  }, [element, setRect]);
  useEffect(() => {
    if (!element) {
      return;
    }
    return listenToWindow("resize", updateRect);
  }, [element, updateRect]);
  useEffect(() => {
    if (!element || !scroll) {
      return;
    }
    return listenToWindow("scroll", ({target}) => {
      if (doesEventTargetContainElement(target, element)) {
        updateRect();
      }
    });
  }, [scroll, element, updateRect]);
  useEffect(() => {
    if (!element || !transitionEnd) {
      return;
    }
    return listenToWindow("transitionend", ({target}) => {
      if (target === element || doesEventTargetContainElement(target, element)) {
        updateRect();
      }
    });
  }, [transitionEnd, element, updateRect]);
  useEffect(() => {
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(updateRect);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, updateRect]);
  useIsomorphicLayoutEffect(updateRect);
  return [setElement, rect];
}

export { INITIAL_RECT, useRect };
//# sourceMappingURL=react-use-rect.esm.js.map
